/**
 * Terminos y condiciones de compra.
 *
 * Los pide la Ley 19.496 del Consumidor (art. 12 A) y su reglamento de
 * comercio electronico: el comprador tiene que poder leerlos antes de pagar,
 * en forma clara, y guardarlos o imprimirlos. Por eso son una pagina publica y
 * el checkout enlaza aca junto al boton de pagar.
 *
 * Redactado con lo que el sitio hace de verdad (venta por paquete, stock
 * verificado con Random, Webpay, despacho por region y comuna) y aprobado por
 * el cliente el 02-10-2026. Si el sitio cambia algo de eso, el texto cambia en
 * el mismo turno.
 */
import { Link } from 'react-router-dom';

import { DocumentoLegal } from '../componentes/legal/DocumentoLegal';
import { SITIO } from '../configuracion/sitio';
import { rutas } from '../utilidades/rutas';

export function Terminos() {
  return (
    <DocumentoLegal
      titulo="Términos y condiciones"
      descripcion={`Condiciones de compra en ${SITIO.nombre}: precios, pago, despacho, retracto, garantía y contacto.`}
      ruta={rutas.terminos()}
      actualizado="2 de octubre de 2026"
    >
      <section>
        <h2>1. Quiénes somos</h2>
        <p>
          Este sitio es la tienda en línea de {SITIO.nombreLargo}, de {SITIO.razonSocial}, RUT{' '}
          {SITIO.rut}, con domicilio en {SITIO.direccion.calle}, {SITIO.direccion.comuna},{' '}
          {SITIO.direccion.region}. Puedes contactarnos al correo{' '}
          <a href={`mailto:${SITIO.email}`}>{SITIO.email}</a>, al teléfono {SITIO.telefono} o por
          WhatsApp al {SITIO.whatsappVisible}.
        </p>
        <p>
          Al comprar en este sitio aceptas estos términos y condiciones. Puedes guardarlos o
          imprimirlos desde tu navegador cuando quieras.
        </p>
      </section>

      <section>
        <h2>2. Productos y precios</h2>
        <p>
          Los precios están en pesos chilenos e incluyen IVA. El precio que pagas es el que se
          muestra al momento de pagar, y no cambia después de que el pago es aprobado.
        </p>
        <p>
          Algunos productos se venden por paquete o caja (por ejemplo, una caja de clavos). En ese
          caso el precio, el stock y la cantidad que eliges son por paquete, y en la ficha se
          indica cuántas unidades trae cada uno.
        </p>
        <p>
          Las fotos son referenciales: el color o el empaque pueden variar según el lote del
          fabricante. Las medidas y características son las que indica la ficha del producto.
        </p>
      </section>

      <section>
        <h2>3. Stock</h2>
        <p>
          El stock que ves se actualiza con nuestro sistema de inventario durante el día, y se
          vuelve a confirmar al entrar al pago y justo antes de cobrar. Si un producto se agotó
          mientras comprabas, te avisamos antes de pagar para que ajustes la cantidad, lo quites o
          lo consultes por WhatsApp.
        </p>
      </section>

      <section>
        <h2>4. Cómo comprar</h2>
        <p>
          Agregas los productos al carro, completas tus datos, eliges la entrega y el documento
          (boleta o factura) y pagas en Webpay. Antes de pagar ves el detalle completo: productos,
          costo de la entrega y total. Cuando el pago es aprobado te mostramos la confirmación y te
          la enviamos por correo con el número de tu pedido.
        </p>
        <p>
          También puedes enviarnos tu pedido por WhatsApp. En ese caso la compra se concreta en esa
          conversación, con las condiciones que acordemos ahí.
        </p>
      </section>

      <section>
        <h2>5. Pago</h2>
        <p>
          Los pagos en el sitio se hacen con Webpay Plus de Transbank, con tarjetas de crédito,
          débito o prepago. Las cuotas, si las hay, dependen de tu banco. Nosotros no vemos ni
          guardamos el número de tu tarjeta: lo ingresas directamente en Webpay.
        </p>
      </section>

      <section>
        <h2>6. Boleta o factura</h2>
        <p>
          Toda compra lleva boleta o factura electrónica, la que elijas al comprar, y te llega al
          correo que nos dejaste. Para factura necesitamos el RUT, la razón social, el giro y la
          dirección de facturación de la empresa; el documento se emite con los datos que
          ingreses, así que revísalos antes de pagar.
        </p>
      </section>

      <section>
        <h2>7. Despacho y retiro</h2>
        <p>
          El costo y el plazo estimado del despacho dependen de la región y la comuna de destino, y
          los ves antes de pagar. Sobre el monto que se indique en el sitio, el despacho es
          gratis. Los plazos son estimados y se cuentan desde la confirmación del pago.
        </p>
        <p>
          También puedes retirar gratis en nuestra sala de ventas, {SITIO.direccion.calle},{' '}
          {SITIO.direccion.comuna} ({SITIO.horario}), en el plazo que se indica al comprar.
        </p>
        <p>
          Por temporadas podemos suspender el despacho o el retiro. Cuando no se ofrece ninguno de
          los dos, los pedidos se coordinan por WhatsApp.
        </p>
      </section>

      <section>
        <h2>8. Derecho de retracto</h2>
        <p>
          En las compras a distancia la ley te da derecho a retractarte dentro de 10 días desde que
          recibes el producto, salvo que la tienda lo excluya expresamente antes de la compra.
        </p>
      </section>

      <section>
        <h2>9. Garantía legal</h2>
        <p>
          Si un producto viene con fallas, no corresponde a lo que compraste o no sirve para el uso
          al que está destinado, tienes una garantía legal de <strong>6 meses</strong> desde que lo
          recibes. Puedes elegir entre la reparación gratuita, el cambio o la devolución de lo
          pagado. Para hacerla efectiva necesitas la boleta o factura, y nos escribes a{' '}
          <a href={`mailto:${SITIO.email}`}>{SITIO.email}</a> o te acercas a la sala de ventas.
        </p>
        <p>
          Si el fabricante ofrece una garantía propia, se suma a la garantía legal; no la
          reemplaza.
        </p>
      </section>

      <section>
        <h2>10. Tus datos personales</h2>
        <p>
          Cómo usamos los datos que nos entregas al comprar está explicado en la{' '}
          <Link to={rutas.privacidad()}>política de privacidad</Link>.
        </p>
      </section>

      <section>
        <h2>11. Consultas y reclamos</h2>
        <p>
          Cualquier consulta o reclamo sobre una compra la recibimos en{' '}
          <a href={`mailto:${SITIO.email}`}>{SITIO.email}</a>, al {SITIO.telefono} o por WhatsApp
          al {SITIO.whatsappVisible}. Si no quedas conforme, puedes recurrir al Servicio Nacional
          del Consumidor (SERNAC) o al juzgado de policía local que corresponda.
        </p>
        <p>
          Estos términos se rigen por la ley chilena, en especial la Ley 19.496 sobre Protección de
          los Derechos de los Consumidores. Podemos actualizarlos; cada compra se rige por los que
          estaban publicados al momento de pagarla.
        </p>
      </section>
    </DocumentoLegal>
  );
}
